import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { demandasApi, type SubmeterDemandaInput, type Urgencia } from '../api/demandas.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import { mensagemDeErro } from '../mensagens/mensagens.js';
import { ErroRequisicao } from '../api/cliente.js';

const URGENCIAS: Urgencia[] = ['baixa', 'media', 'alta', 'critica'];

function CorUrgencia({ urgencia }: { urgencia: Urgencia }) {
  const cor = urgencia === 'critica' ? 'text-status-vermelho' : urgencia === 'alta' ? 'text-status-ambar' : 'text-grafite';
  return <span className={`fonte-mono text-xs uppercase ${cor}`}>{urgencia}</span>;
}

function FormularioSubmeterDemanda({ onCriado }: { onCriado: () => void }) {
  const [form, setForm] = useState<SubmeterDemandaInput>({
    titulo: '',
    problema: '',
    beneficioEsperado: '',
    solicitante: '',
    urgencia: 'media',
  });
  const [erro, setErro] = useState<string | null>(null);

  const mutacao = useMutation({
    mutationFn: () => demandasApi.submeter(form),
    onSuccess: () => {
      setForm({ titulo: '', problema: '', beneficioEsperado: '', solicitante: '', urgencia: 'media' });
      onCriado();
    },
    onError: (e) => setErro(e instanceof ErroRequisicao ? mensagemDeErro(e.erro.codigo) : 'Não foi possível submeter a demanda.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErro(null);
        mutacao.mutate();
      }}
      className="flex flex-col gap-3 rounded-lg border border-borda bg-superficie p-4"
    >
      <h2 className="text-sm font-semibold text-ink">Nova demanda</h2>
      <input
        required
        placeholder="Título"
        value={form.titulo}
        onChange={(e) => setForm({ ...form, titulo: e.target.value })}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <textarea
        required
        placeholder="Problema"
        value={form.problema}
        onChange={(e) => setForm({ ...form, problema: e.target.value })}
        rows={2}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <textarea
        required
        placeholder="Benefício esperado"
        value={form.beneficioEsperado}
        onChange={(e) => setForm({ ...form, beneficioEsperado: e.target.value })}
        rows={2}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <input
        required
        placeholder="Solicitante"
        value={form.solicitante}
        onChange={(e) => setForm({ ...form, solicitante: e.target.value })}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <select
        value={form.urgencia}
        onChange={(e) => setForm({ ...form, urgencia: e.target.value as Urgencia })}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      >
        {URGENCIAS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      {erro && <p className="text-sm text-status-vermelho">{erro}</p>}
      <button
        type="submit"
        disabled={mutacao.isPending}
        className="self-start rounded bg-aco px-4 py-2 text-sm font-medium text-white hover:bg-aco-700 disabled:opacity-50"
      >
        {mutacao.isPending ? 'Enviando…' : 'Submeter demanda'}
      </button>
    </form>
  );
}

// SPEC-UX §4.2 — fila de triagem ordenada por score. A composição do score
// expandível por linha (D9) e o cartão de VeredictoCapacidade ficam para
// quando a tela de detalhe da demanda existir; esta é a fila principal.
export function Intake() {
  const queryClient = useQueryClient();
  const { data: fila, isLoading } = useQuery({ queryKey: ['demandas', 'fila'], queryFn: demandasApi.fila });

  function recarregar() {
    queryClient.invalidateQueries({ queryKey: ['demandas'] });
  }

  return (
    <div className="flex flex-col gap-6 p-8">
      <h1 className="text-xl font-semibold text-ink">Intake</h1>

      <FormularioSubmeterDemanda onCriado={recarregar} />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-grafite">Fila de triagem</h2>
        {isLoading && <p className="text-sm text-grafite">Carregando…</p>}
        {!isLoading && (!fila || fila.length === 0) && (
          <VazioQueEnsina explicacao="Nenhuma demanda em triagem. Demandas entram na fila assim que são submetidas e passam para triagem." />
        )}
        {fila && fila.length > 0 && (
          <ul className="flex flex-col gap-2">
            {fila.map((demanda) => (
              <li key={demanda.id} className="flex items-center justify-between rounded border border-borda bg-superficie px-4 py-3">
                <div>
                  <p className="text-sm text-ink">{demanda.titulo}</p>
                  <p className="text-xs text-grafite">{demanda.estado}</p>
                </div>
                <CorUrgencia urgencia={demanda.urgencia} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
